import React, { useEffect, useState, useCallback } from "react";
import AdminLayout from "@/components/layout/AdminLayout";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Megaphone, Check, X, Send, Download, Copy, Trash2, Sparkles, Settings2 } from "lucide-react";

const TABS = [
  { key: "draft", label: "Draft" },
  { key: "approved", label: "Approved / Ready" },
  { key: "posted", label: "Posted" },
  { key: "manual_posted", label: "Manual Posted" },
  { key: "rejected", label: "Rejected" },
];

const API_BASE = process.env.REACT_APP_BACKEND_URL;

// Authenticated image loader (img src can't send Bearer token)
function CardImage({ draftId, className }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let url;
    api.get(`/admin/social/drafts/${draftId}/card.png`, { responseType: "blob" })
      .then((res) => { url = URL.createObjectURL(res.data); setSrc(url); })
      .catch(() => {});
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [draftId]);
  return src ? <img src={src} alt="social card" className={className} data-testid={`draft-card-img-${draftId}`} /> : <div className={`${className} bg-slate-100 animate-pulse`} />;
}

export default function AdminSocial() {
  const [tab, setTab] = useState("draft");
  const [data, setData] = useState({ items: [], counts: {}, x_configured: false });
  const [camps, setCamps] = useState([]);
  const [genCamp, setGenCamp] = useState("");

  const load = useCallback(() => {
    api.get("/admin/social/drafts", { params: { status: tab } }).then(({ data }) => setData(data)).catch(() => {});
  }, [tab]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/camps").then(({ data }) => { setCamps(data.items || []); if (data.items?.[0]) setGenCamp(data.items[0].id); }); }, []);

  const act = async (fn, okMsg) => {
    try { await fn(); if (okMsg) toast.success(okMsg); load(); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || e.message); }
  };

  const approve = (d) => act(() => api.patch(`/admin/social/drafts/${d.id}`, { status: "approved" }), "Approved — ready to post");
  const reject = (d) => act(() => api.patch(`/admin/social/drafts/${d.id}`, { status: "rejected" }), "Rejected");
  const del = (d) => act(() => api.delete(`/admin/social/drafts/${d.id}`), "Deleted");
  const saveText = (d, text) => act(() => api.patch(`/admin/social/drafts/${d.id}`, { suggested_text: text }), "Text saved");
  const post = (d) => act(async () => {
    const { data } = await api.post(`/admin/social/drafts/${d.id}/post`);
    toast.success(data.mode === "x" ? "Posted to X!" : "Marked as manually posted");
  });

  const downloadCard = async (d) => {
    try {
      const res = await api.get(`/admin/social/drafts/${d.id}/card.png`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `cbr-${d.template_type}-${d.player?.last_name || "card"}.png`;
      a.click(); URL.revokeObjectURL(url);
    } catch (e) { toast.error("Download failed"); }
  };

  const copyText = (d) => { navigator.clipboard.writeText(d.suggested_text); toast.success("Post text copied"); };

  const generate = (kind) => act(async () => {
    const { data } = await api.post(`/admin/social/camps/${genCamp}/generate-${kind}`);
    toast.success(`${data.created} ${kind === "awards" ? "award" : "invite"} draft${data.created !== 1 ? "s" : ""} created`);
    setTab("draft");
  });

  return (
    <AdminLayout>
      <div className="p-8 max-w-6xl">
        <div className="flex items-center gap-2 label-overline text-[#E63946]"><Megaphone className="w-4 h-4" /> CBR Media Engine</div>
        <h1 data-testid="social-title" className="font-display text-4xl uppercase tracking-tighter mt-2">Social Posts</h1>
        <div className="flex items-center gap-2 mt-2">
          <span data-testid="x-status-badge" className={`inline-flex items-center gap-1 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider rounded-sm ${data.x_configured ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}>
            {data.x_configured ? "X Connected — live posting" : "Manual Mode — X keys not set"}
          </span>
          <span className="text-xs text-slate-500">Every post requires admin approval. Nothing auto-posts.</span>
        </div>

        {/* Generate toolbar */}
        <div className="mt-6 bg-white border border-slate-200 rounded-sm p-4 flex flex-wrap items-end gap-3">
          <div className="flex items-center gap-2 label-overline text-[#0A192F]"><Sparkles className="w-4 h-4 text-[#E63946]" /> Generate Camp Content</div>
          <select data-testid="gen-camp-select" value={genCamp} onChange={(e) => setGenCamp(e.target.value)} className="border border-slate-300 rounded-sm px-3 py-2 text-sm bg-white min-w-[240px]">
            {camps.map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}
          </select>
          <button data-testid="gen-awards-btn" onClick={() => generate("awards")} className="bg-[#0A192F] text-white px-4 py-2 text-xs font-semibold uppercase tracking-wider rounded-sm">Camp Awards</button>
          <button data-testid="gen-invites-btn" onClick={() => generate("invites")} className="bg-[#0A192F] text-white px-4 py-2 text-xs font-semibold uppercase tracking-wider rounded-sm">Top 40 Invites</button>
          <span className="text-[11px] text-slate-500 ml-2">Only players with media consent + tagging enabled are included.</span>
        </div>

        {/* Status tabs */}
        <div className="flex flex-wrap gap-2 mt-6 border-b border-slate-200 pb-3">
          {TABS.map((t) => (
            <button key={t.key} data-testid={`social-tab-${t.key}`} onClick={() => setTab(t.key)}
              className={`px-4 py-2 text-xs font-semibold uppercase tracking-wider rounded-sm ${tab === t.key ? "bg-[#E63946] text-white" : "bg-white border border-slate-300 text-[#0A192F] hover:bg-slate-50"}`}>
              {t.label} <span className="ml-1 opacity-70">{data.counts?.[t.key] ?? 0}</span>
            </button>
          ))}
        </div>

        {/* Drafts */}
        <div className="grid md:grid-cols-2 gap-5 mt-6">
          {(() => {
            const visible = (data.items || []).filter((d) => d.status === tab);
            if (visible.length === 0) {
              return (
                <div className="md:col-span-2 bg-white border border-slate-200 rounded-sm p-12 text-center">
                  <Megaphone className="w-10 h-10 mx-auto text-slate-300" />
                  <div className="font-display text-xl uppercase mt-3">No {tab.replace("_", " ")} posts</div>
                  <p className="text-sm text-slate-500 mt-1">Verified metrics that cross a rule auto-queue here, or generate camp content above.</p>
                </div>
              );
            }
            return visible.map((d) => (
              <DraftCard key={d.id} d={d} xConfigured={data.x_configured}
                onApprove={approve} onReject={reject} onDelete={del} onPost={post}
                onDownload={downloadCard} onCopy={copyText} onSaveText={saveText} />
            ));
          })()}
        </div>
      </div>
    </AdminLayout>
  );
}

function DraftCard({ d, xConfigured, onApprove, onReject, onDelete, onPost, onDownload, onCopy, onSaveText }) {
  const [text, setText] = useState(d.suggested_text);
  const dirty = text !== d.suggested_text;
  const terminal = d.status === "posted" || d.status === "manual_posted";

  return (
    <div data-testid={`draft-${d.id}`} className="bg-white border border-slate-200 rounded-sm overflow-hidden flex flex-col">
      <CardImage draftId={d.id} className="w-full aspect-square object-cover bg-[#0A192F]" />
      <div className="p-4 flex flex-col gap-3 flex-1">
        <div className="flex items-center justify-between">
          <span className="label-overline text-[#E63946]">{d.card_spec?.accent}</span>
          <div className="flex items-center gap-1.5">
            {d.is_regenerated && <span data-testid={`draft-regenerated-${d.id}`} className="text-[9px] font-semibold uppercase tracking-wider bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-sm">Regenerated</span>}
            <span className="text-[10px] uppercase tracking-wider text-slate-400">{d.template_type.replace(/_/g, " ")}</span>
          </div>
        </div>
        <div className="text-sm font-semibold">{d.player?.first_name} {d.player?.last_name} <span className="text-slate-400 font-normal">· {d.player?.primary_position} {d.player?.graduation_year}</span></div>
        <textarea
          data-testid={`draft-text-${d.id}`}
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={6}
          className="w-full text-xs border border-slate-200 rounded-sm p-2 font-mono resize-y"
        />
        {dirty && (
          <button data-testid={`draft-savetext-${d.id}`} onClick={() => onSaveText(d, text)} className="self-start text-[10px] font-semibold uppercase tracking-wider text-[#E63946]">Save text changes</button>
        )}
        <div className="flex flex-wrap gap-2 mt-auto pt-2 border-t border-slate-100">
          {d.status === "draft" && (
            <>
              <Btn testid={`approve-${d.id}`} onClick={() => onApprove(d)} cls="bg-emerald-600 text-white"><Check className="w-3.5 h-3.5" /> Approve</Btn>
              <Btn testid={`reject-${d.id}`} onClick={() => onReject(d)} cls="bg-white border border-slate-300"><X className="w-3.5 h-3.5" /> Reject</Btn>
            </>
          )}
          {d.status === "approved" && (
            <Btn testid={`post-${d.id}`} onClick={() => onPost(d)} cls="bg-[#E63946] text-white"><Send className="w-3.5 h-3.5" /> {xConfigured ? "Post to X" : "Mark Posted"}</Btn>
          )}
          {terminal && <span className="text-[10px] uppercase tracking-wider px-2 py-1.5 bg-slate-100 rounded-sm text-slate-600">{d.posted_mode === "x" ? "Posted to X" : "Posted manually"}</span>}
          <Btn testid={`download-${d.id}`} onClick={() => onDownload(d)} cls="bg-white border border-slate-300"><Download className="w-3.5 h-3.5" /> Image</Btn>
          <Btn testid={`copy-${d.id}`} onClick={() => onCopy(d)} cls="bg-white border border-slate-300"><Copy className="w-3.5 h-3.5" /> Copy</Btn>
          <Btn testid={`delete-${d.id}`} onClick={() => onDelete(d)} cls="bg-white border border-slate-300 text-[#E63946] ml-auto"><Trash2 className="w-3.5 h-3.5" /></Btn>
        </div>
      </div>
    </div>
  );
}

function Btn({ children, onClick, cls, testid }) {
  return (
    <button data-testid={testid} onClick={onClick} className={`inline-flex items-center gap-1 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider rounded-sm ${cls}`}>
      {children}
    </button>
  );
}
